import { getCanonicalScoreKey } from '@/domains/scoring';
import { GlobalState, GlobalStateActions, IGlobalStateSidebarScorecard } from './types';
import {
  IAppConfig,
  ICertification,
  IManagedUserSummary,
  IManagerProduct,
  IManagerTeamSummary,
  IPersona,
  ISimulation,
  ISimulationScore,
  IAppNotification,
  IUser,
  IUserBadge,
  ApiHealthCheckResponse,
} from '@/types';

/**
 * Typed wrapper for Zustand's `set` function used by action factories.
 *
 * Accepts either a partial state object or an updater function that receives
 * the current `GlobalState`. The optional `action` string is used for devtools
 * labeling and debugging.
 */
type StoreSet = (
  state: Partial<GlobalState> | ((state: GlobalState) => Partial<GlobalState>),
  replace?: boolean,
  action?: string,
) => void;

/**
 * Typed wrapper for Zustand's `get` function used by selector-style actions.
 *
 * Returns the latest `GlobalState` snapshot so actions can derive data without
 * triggering component re-renders.
 */
type StoreGet = () => GlobalState;

/**
 * Sets the `currentUser` in global state.
 *
 * Keeps the user object nullable to support logout and unauthenticated states,
 * while tagging the update for devtools inspection.
 */
export const setCurrentUser = (set: StoreSet) => (user: IUser | null) => {
  return set({ currentUser: user }, false, 'setCurrentUser');
};

/**
 * Replaces the entire `currentUserBadges` array.
 *
 * This is useful when badges are fetched from the API or recalculated after
 * profile updates.
 */
export const setCurrentUserBadges = (set: StoreSet) => (badges: IUserBadge[]) => {
  return set({ currentUserBadges: badges }, false, 'setCurrentUserBadges');
};

/**
 * Sets the manager team summary payload.
 *
 * Keeps summary data in one place for manager dashboards.
 */
export const setManagersTeamSummary =
  (set: StoreSet) => (managersTeamSummary: IManagerTeamSummary) => {
    return set({ managersTeamSummary }, false, 'setManagersTeamSummary');
  };

/**
 * Sets the active manager product context.
 *
 * Allows manager flows to scope views to a selected product.
 */
export const setManagerActiveProduct =
  (set: StoreSet) => (product: IManagerProduct | null) => {
    return set({ managerActiveProduct: product }, false, 'setManagerActiveProduct');
  };

/**
 * Sets the active managed user summary.
 *
 * Supports selecting/deselecting a user in manager views.
 */
export const setManagerActiveUser =
  (set: StoreSet) => (managerActiveUser: IManagedUserSummary | null) => {
    return set({ managerActiveUser }, false, 'setManagerActiveUser');
  };

/**
 * Stores the active managed user ID.
 *
 * Useful when only the identifier is known before loading the full user data.
 */
export const setManagerActiveUserId = (set: StoreSet) => (userId: string | null) => {
  return set({ managerActiveUserId: userId }, false, 'setManagerActiveUserId');
};

/**
 * Toggles the manager user loading flag.
 *
 * Drives spinners or skeletons while the active managed user is fetched.
 */
export const setManagerActiveUserIsLoading = (set: StoreSet) => (isLoading: boolean) => {
  return set({ managerActiveUserIsLoading: isLoading }, false, 'setManagerActiveUserIsLoading');
};

/**
 * Toggles the manager scorecard loading flag.
 *
 * Separates scorecard loading state from general manager user loading.
 */
export const setManagerActiveUserScorecardIsLoading = (set: StoreSet) => (isLoading: boolean) => {
  return set(
    { managerActiveUserScorecardIsLoading: isLoading },
    false,
    'setManagerActiveUserScorecardIsLoading',
  );
};

/**
 * Updates the active managed user by ID in `managerActiveUser`.
 *
 * The update is shallow-merged into the matching user.
 */
export const updateManagerActiveUser =
  (set: StoreSet) => (updates: Partial<IManagedUserSummary>) => {
    return set(
      (state) => {
        if (!state.managerActiveUser) {
          return {};
        }

        return {
          managerActiveUser: { ...state.managerActiveUser, ...updates },
        };
      },
      false,
      'updateManagerActiveUser',
    );
  };

/**
 * Replaces the full `personas` array.
 *
 * Intended for bulk loading or refreshing persona data.
 */
export const setPersonas = (set: StoreSet) => (personas: IPersona[]) => {
  return set({ personas }, false, 'setPersonas');
};

/**
 * Appends a persona to the `personas` array.
 *
 * Keeps existing personas intact while adding the new one.
 */
export const addPersona = (set: StoreSet) => (persona: IPersona) => {
  return set(
    (state) => {
      const personas = [...state.personas, persona];
      return { personas };
    },
    false,
    'addPersona',
  );
};

/**
 * Adds a score using canonical identity for deduplication.
 *
 * Uses the canonical score key (from Phase 5 identity policy) to match existing scores
 * based on their primary identifier (sessionId > uuid > id > unknown). This prevents
 * duplicate entries when the same logical score is upserted via different ID schemes.
 *
 * If a score with the same canonical key exists, it is replaced; otherwise, a new
 * entry is added.
 *
 * ## Example
 * - Score with sessionId:401 and uuid:abc will match existing scores by sessionId first
 * - Scores without sessionId fall back to uuid matching
 * - Fallback ensures consistency across all score identification patterns
 */
export const addScore = (set: StoreSet) => (score: ISimulationScore) => {
  return set(
    (state) => {
      const incomingKey = getCanonicalScoreKey(score);
      const existingIndex = state.scores.findIndex(
        (s) => getCanonicalScoreKey(s) === incomingKey,
      );

      if (existingIndex >= 0) {
        const scores = [...state.scores];
        scores[existingIndex] = score;
        return { scores };
      }

      const scores = [...state.scores, score];
      return { scores };
    },
    false,
    'addScore',
  );
};

/**
 * Updates a persona by ID in the `personas` array.
 *
 * Applies a shallow merge of updates into the matching persona.
 */
export const updatePersona = (set: StoreSet) => (personaId: string, updates: Partial<IPersona>) => {
  return set(
    (state) => {
      const personas = state.personas.map((persona) => {
        return persona.uuid === personaId ? { ...persona, ...updates } : persona;
      });

      return { personas };
    },
    false,
    'updatePersona',
  );
};

/**
 * Updates a scorecard by numeric ID in the `scores` array.
 *
 * Applies a shallow merge of updates into the matching scorecard entry.
 * Looks up the score by its numeric `id` field, which is the primary lookup pattern
 * from legacy call sites (e.g., shareScorecard feature).
 *
 * Note: This function continues to use numeric ID lookup for backward compatibility.
 * For new code paths that need flexible score identification, consider using the
 * canonical score key approach from the Phase 5 identity policy.
 */
export const updateScorecard =
  (set: StoreSet) => (scorecardId: number, updates: Partial<ISimulationScore>) => {
    return set(
      (state) => {
        const scores = state.scores.map((score) => {
          return score.id === scorecardId ? { ...score, ...updates } : score;
        });

        return { scores };
      },
      false,
      'updateScorecard',
    );
  };

/**
 * Replaces the full `certifications` array.
 *
 * Intended for bulk fetching or refreshing certification data.
 */
export const setCertifications = (set: StoreSet) => (certifications: ICertification[]) => {
  return set({ certifications }, false, 'setCertifications');
};

/**
 * Sets the certifications error state.
 *
 * This can be used to trigger error banners or fallback UI when certification
 * data fails to load.
 */
export const setCertificationsError =
  (set: StoreSet) => (error: { message: string; status: number } | null) => {
    return set({ certificationsError: error }, false, 'setCertificationsError');
  };

/**
 * Updates a certification by ID in `certifications`.
 *
 * Applies a shallow merge of updates into the matching certification entry.
 */
export const updateCertification =
  (set: StoreSet) => (certificationId: string, updates: Partial<ICertification>) => {
    return set(
      (state) => ({
        certifications: state.certifications.map((cert) =>
          cert.uuid === certificationId ? { ...cert, ...updates } : cert,
        ),
      }),
      false,
      'updateCertification',
    );
  };

/**
 * Replaces the full `simulations` array.
 *
 * Useful for initial hydration or bulk refreshes.
 */
export const setSimulations = (set: StoreSet) => (simulations: ISimulation[]) => {
  return set({ simulations }, false, 'setSimulations');
};

/**
 * Replaces the full `scores` array.
 *
 * Typically used after fetching score data in bulk.
 */
export const setScores = (set: StoreSet) => (scores: ISimulationScore[]) => {
  return set({ scores }, false, 'setScores');
};

/**
 * Replaces the full notifications stream.
 *
 * Notifications are sorted from newest to oldest based on `createdAt`.
 */
export const setNotifications = (set: StoreSet) => (notifications: IAppNotification[]) => {
  const sorted = [...notifications].sort((a, b) => b.createdAt - a.createdAt);
  return set({ notifications: sorted }, false, 'setNotifications');
};

/**
 * Adds a single notification to the stream.
 *
 * If a matching notification exists, it is replaced.
 */
export const addNotification = (set: StoreSet) => (notification: IAppNotification) => {
  return set(
    (state) => {
      const existingIndex = state.notifications.findIndex((item) => item.id === notification.id);
      if (existingIndex >= 0) {
        const notifications = [...state.notifications];
        notifications.splice(existingIndex, 1, notification);
        return {
          notifications: notifications.sort((a, b) => b.createdAt - a.createdAt),
        };
      }

      return {
        notifications: [notification, ...state.notifications].sort(
          (a, b) => b.createdAt - a.createdAt,
        ),
      };
    },
    false,
    'addNotification',
  );
};

/**
 * Adds multiple notifications to the stream.
 *
 * Incoming notifications overwrite existing ones by ID.
 */
export const addNotifications = (set: StoreSet) => (notifications: IAppNotification[]) => {
  return set(
    (state) => {
      const byId = new Map<string, IAppNotification>();

      state.notifications.forEach((item) => {
        byId.set(item.id, item);
      });

      notifications.forEach((item) => {
        byId.set(item.id, item);
      });

      const merged = Array.from(byId.values()).sort((a, b) => b.createdAt - a.createdAt);
      return { notifications: merged };
    },
    false,
    'addNotifications',
  );
};

/**
 * Removes a notification from the stream.
 */
export const removeNotification = (set: StoreSet) => (notificationId: string) => {
  return set(
    (state) => ({
      notifications: state.notifications.filter((item) => item.id !== notificationId),
    }),
    false,
    'removeNotification',
  );
};

/**
 * Marks a notification as read.
 */
export const markNotificationRead = (set: StoreSet) => (notificationId: string) => {
  return set(
    (state) => ({
      notifications: state.notifications.map((item) =>
        item.id === notificationId ? { ...item, isRead: true } : item,
      ),
    }),
    false,
    'markNotificationRead',
  );
};

/**
 * Marks all notifications as read.
 */
export const markAllNotificationsRead = (set: StoreSet) => () => {
  return set(
    (state) => ({
      notifications: state.notifications.map((item) => ({ ...item, isRead: true })),
    }),
    false,
    'markAllNotificationsRead',
  );
};

/**
 * Clears all notifications from the stream.
 */
export const clearNotifications = (set: StoreSet) => () => {
  return set({ notifications: [] }, false, 'clearNotifications');
};

/**
 * Sets the active certificate entity.
 *
 * A `null` value clears the current selection.
 */
export const setActiveCertificate = (set: StoreSet) => (certificate: ICertification | null) => {
  return set({ activeCertificate: certificate }, false, 'setActiveCertificate');
};

/**
 * Sets the active certification entity.
 *
 * A `null` value clears the current selection.
 */
export const setActiveCertification = (set: StoreSet) => (certification: ICertification | null) => {
  return set({ activeCertification: certification }, false, 'setActiveCertification');
};

/**
 * Sets the active simulation entity.
 *
 * A `null` value clears the current selection.
 */
export const setActiveSimulation = (set: StoreSet) => (simulation: ISimulation | null) => {
  return set({ activeSimulation: simulation }, false, 'setActiveSimulation');
};

/**
 * Sets the active persona entity.
 *
 * A `null` value clears the current selection.
 */
export const setActivePersona = (set: StoreSet) => (persona: IPersona | null) => {
  return set({ activePersona: persona }, false, 'setActivePersona');
};

/**
 * Sets the active score entity.
 *
 * A `null` value clears the current selection.
 */
export const setActiveScore = (set: StoreSet) => (score: ISimulationScore | null) => {
  return set({ activeScore: score }, false, 'setActiveScore');
};

/**
 * Sets the active session ID.
 *
 * `null` is used to clear the current session context.
 */
export const setActiveSessionId = (set: StoreSet) => (sessionId: number | null) => {
  return set({ activeSessionId: sessionId }, false, 'setActiveSessionId');
};

/**
 * Sets the active user entity.
 *
 * A `null` value clears the current selection.
 */
export const setActiveUser = (set: StoreSet) => (user: IUser | null) => {
  return set({ activeUser: user }, false, 'setActiveUser');
};

/**
 * Clears all "active" selection state.
 *
 * Useful when navigating away from detail pages or resetting view context.
 */
export const resetActives = (set: StoreSet) => () => {
  return set(
    {
      activeCertification: null,
      activePersona: null,
      activeScore: null,
      activeSessionId: null,
      activeSimulation: null,
      activeUser: null,
    },
    false,
    'resetActives',
  );
};

/**
 * Toggles the global loading flag.
 *
 * Intended for high-level page loading or initial data hydration.
 */
export const setLoading = (set: StoreSet) => (isLoading: boolean) => {
  return set({ isLoading }, false, 'setLoading');
};

/**
 * Toggles the global printing flag.
 *
 * Intended for high-level page printing or print preview.
 */
export const setIsPrinting = (set: StoreSet) => (isPrinting: boolean) => {
  return set({ isPrinting }, false, 'setIsPrinting');
};

/**
 * Stores the latest global error payload.
 *
 * This may be used by error banners or logging surfaces.
 */
export const setError = (set: StoreSet) => (error: any) => {
  return set({ error }, false, 'setError');
};

/**
 * Sets the API health status string.
 *
 * This can be used to trigger health-related UI changes or alerts.
 * The string can represent various health states (e.g., "healthy", "degraded", "down").
 */
export const setApiHealth = (set: StoreSet) => (health: ApiHealthCheckResponse) => {
  return set({ apiHealth: health }, false, 'setApiHealth');
};

/**
 * Sets the application configuration payload.
 *
 * Used for feature flags, branding, or configuration-driven UI.
 */
export const setAppConfig = (set: StoreSet) => (appConfig: IAppConfig) => {
  return set({ appConfig }, false, 'setAppConfig');
};

/**
 * Records whether usage consent has been accepted.
 *
 * Helps enforce legal and analytics consent flows.
 */
export const setUsageConsentAccepted = (set: StoreSet) => (accepted: boolean) => {
  return set({ usageConsentAccepted: accepted }, false, 'setUsageConsentAccepted');
};

/**
 * Toggles whether to fetch primary data.
 *
 * Useful for bypassing refresh logic when data is already available.
 */
export const setFetchData = (set: StoreSet) => (fetch: boolean) => {
  return set({ fetchData: fetch }, false, 'setFetchData');
};

/**
 * Toggles whether to fetch manager-specific data.
 *
 * Helps separate manager refresh flows from standard user data.
 */
export const setFetchManagerData = (set: StoreSet) => (fetch: boolean) => {
  return set({ fetchManagerData: fetch }, false, 'setFetchManagerData');
};

/**
 * Creates a selector-like action that finds a persona by ID.
 *
 * Returns `undefined` when no match exists.
 */
export const getPersonaById = (get: StoreGet) => (id: string) => {
  return get().personas.find((p) => p.uuid === id);
};

/**
 * Creates a selector-like action that finds a simulation by ID.
 *
 * Returns `undefined` when no match exists.
 */
export const getSimulationById = (get: StoreGet) => (id: string) => {
  return get().simulations.find((s) => s.uuid === id);
};

/**
 * Creates a selector-like action that finds a certification by ID.
 *
 * Returns `undefined` when no match exists.
 */
export const getCertificationById = (get: StoreGet) => (id: string) => {
  return get().certifications.find((c) => c.uuid === id);
};

/**
 * Toggles the visibility of the outage alert banner.
 *
 * When `true`, the banner is shown across the app to inform users of an outage.
 * When `false`, the banner is hidden.
 */
export const setShowOutageAlert = (set: StoreSet) => (show: boolean) => {
  return set({ showOutageAlert: show }, false, 'setShowOutageAlert');
};

/**
 * Sets the sidebar scorecard data structure, which includes the score, related certification,
 * simulation, and user name.
 *
 * This is used to populate the sidebar with relevant information about a specific scorecard.
 * A `null` value clears the current sidebar scorecard context.
 */
export const setSidebarScorecard =
  (set: StoreSet) => (sidebarScorecard: IGlobalStateSidebarScorecard | null) => {
    return set({ sidebarScorecard }, false, 'setSidebarScorecard');
  };

/**
 * Sets the sidebar certificate context, which may be used to display related
 * information in the sidebar.
 *
 * A `null` value clears the current sidebar certificate context.
 */
export const setSidebarCertificate =
  (set: StoreSet) => (sidebarCertificate: IGlobalStateSidebarScorecard | null) => {
    return set({ sidebarCertificate }, false, 'setSidebarCertificate');
  };

/**
 * Builds the complete `GlobalStateActions` object from individual factories.
 *
 * Centralizes action creation so the store can spread a single object and keep
 * action wiring consistent and type-safe.
 */
export const createGlobalStoreActions = (set: StoreSet, get: StoreGet): GlobalStateActions => {
  return {
    addNotification: addNotification(set),
    addNotifications: addNotifications(set),
    addPersona: addPersona(set),
    addScore: addScore(set),
    clearNotifications: clearNotifications(set),
    getCertificationById: getCertificationById(get),
    getPersonaById: getPersonaById(get),
    getSimulationById: getSimulationById(get),
    markAllNotificationsRead: markAllNotificationsRead(set),
    markNotificationRead: markNotificationRead(set),
    removeNotification: removeNotification(set),
    resetActives: resetActives(set),
    setActiveCertificate: setActiveCertificate(set),
    setActiveCertification: setActiveCertification(set),
    setActivePersona: setActivePersona(set),
    setActiveScore: setActiveScore(set),
    setActiveSessionId: setActiveSessionId(set),
    setActiveSimulation: setActiveSimulation(set),
    setActiveUser: setActiveUser(set),
    setApiHealth: setApiHealth(set),
    setAppConfig: setAppConfig(set),
    setCertifications: setCertifications(set),
    setCertificationsError: setCertificationsError(set),
    setCurrentUser: setCurrentUser(set),
    setCurrentUserBadges: setCurrentUserBadges(set),
    setError: setError(set),
    setFetchData: setFetchData(set),
    setFetchManagerData: setFetchManagerData(set),
    setIsPrinting: setIsPrinting(set),
    setLoading: setLoading(set),
    setManagerActiveProduct: setManagerActiveProduct(set),
    setManagerActiveUser: setManagerActiveUser(set),
    setManagerActiveUserId: setManagerActiveUserId(set),
    setManagerActiveUserIsLoading: setManagerActiveUserIsLoading(set),
    setManagerActiveUserScorecardIsLoading: setManagerActiveUserScorecardIsLoading(set),
    setManagersTeamSummary: setManagersTeamSummary(set),
    setNotifications: setNotifications(set),
    setPersonas: setPersonas(set),
    setScores: setScores(set),
    setShowOutageAlert: setShowOutageAlert(set),
    setSidebarCertificate: setSidebarCertificate(set),
    setSidebarScorecard: setSidebarScorecard(set),
    setSimulations: setSimulations(set),
    setUsageConsentAccepted: setUsageConsentAccepted(set),
    updateCertification: updateCertification(set),
    updateManagerActiveUser: updateManagerActiveUser(set),
    updatePersona: updatePersona(set),
    updateScorecard: updateScorecard(set),
  };
};
